/* Dialog de reset de senha de um operador (ADR-0709). super_admin define uma nova senha
   (≥12 chars); sem convite/first-login no MVP. locked durante a mutation (ADR-0216). */
import { resetStaffPasswordBody, type StaffUserDto } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@planuze/ui';
import { useState } from 'react';
import { useStaffManagementContent } from '../../content/sections/staff-management.js';
import { useResetStaffPassword } from '../../hooks/use-staff.js';
import { staffErrorMessage } from './staff-error.js';

interface Props {
  readonly staff: StaffUserDto;
  readonly onClose: () => void;
}

export function ResetPasswordDialog({ staff, onClose }: Props) {
  const copy = useStaffManagementContent();
  const reset = useResetStaffPassword();
  const [password, setPassword] = useState('');
  const valid = resetStaffPasswordBody.safeParse({ password }).success;

  const submit = (): void => {
    reset.mutate(
      { id: staff.id, password },
      {
        onSuccess: () => {
          toast.success(copy.resetPasswordDialog.success);
          onClose();
        },
        onError: (err) => toast.error(staffErrorMessage(err, copy.errors)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(next) => !next && !reset.isPending && onClose()}>
      <DialogContent locked={reset.isPending}>
        <DialogHeader>
          <DialogTitle>{copy.resetPasswordDialog.title}</DialogTitle>
          <DialogDescription>{copy.resetPasswordDialog.description(staff.email)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="reset-password-input">{copy.resetPasswordDialog.passwordLabel}</Label>
          <Input
            id="reset-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={copy.resetPasswordDialog.passwordPlaceholder}
            disabled={reset.isPending}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={reset.isPending}>
            {copy.resetPasswordDialog.cancel}
          </Button>
          <Button loading={reset.isPending} disabled={!valid} onClick={submit}>
            {copy.resetPasswordDialog.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
